// src/App.tsx
import React, { useState, useEffect } from 'react';
import './App.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
// import ProjectPage from './pages/ProjectPage';
import config from './config';
import ProjectPageB from './pages/ProjectPageB';
import { DocumentDataProvider } from "../src/context/DocumentDataProvider";


const App: React.FC = () => {
    // Estado para manejar si el usuario está autenticado
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    // Estado para almacenar la información del usuario logeado
    const [userProfile, setUserProfile] = useState<any>(null);
    const [userProjects, setUserProjects] = useState<any[]>([]);
    const [currentProjectId, setCurrentProjectId] = useState<number | null>(null);
    const [currentProjectName, setCurrentProjectName] = useState<string | null>(null);
    const [currentProjectDescription, setCurrentProjectDescription] = useState<string | null>(null);
    // Pending share token saved in state to avoid duplicate consumption across reloads
    const [pendingShareToken, setPendingShareToken] = useState<string | null>(null);

    // Efecto para verificar si hay una sesión activa al cargar la aplicación
    useEffect(() => {
        const storedAuth = localStorage.getItem('isAuthenticated');
        const storedUser = localStorage.getItem('userProfile');
        const storedProjects = localStorage.getItem('userProjects');
        if (storedAuth === 'true' && storedUser) {
            setIsAuthenticated(true);
            setUserProfile(JSON.parse(storedUser));
            setUserProjects(storedProjects ? JSON.parse(storedProjects) : []);
        }
    }, []);

    // If the app is opened with a share token, try to consume it.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('share_token');
        if (!token) return;
        console.log('Found share token in URL:', token);
        const consume = async (t: string) => {
            // If not authenticated, save token for after login (in state)
            if (!isAuthenticated) {
                setPendingShareToken(t);
                return;
            }
            try {
                const resp = await fetch(`${config.API_BASE_URL}/share/consume-share-link.php`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token: t })
                });
                const data = await resp.json();
                console.log('Response from consume-share-link.php:', data);
                if (data.success && data.new_project_id) {
                    handleOpenProject(data.new_project_id, data.name || '', data.description || '');
                } else if (data.need_login) {
                    // Should not happen because we check isAuthenticated above, but handle defensively
                    setPendingShareToken(t);
                } else {
                    // alert('Error consuming share link: ' + (data.message || 'Unknown error'));
                    console.log('Could not consume share link:', data.message || 'Unknown error');
                    console.log('Response data:', data);
                }
            } catch (e) {
                console.error('Error consuming share link', e);
                // alert('Network error consuming share link');
            }
        };

        consume(token);
    }, [isAuthenticated]);

    const handleOpenProject = (projectId: number, name: string, description: string) => {
        setCurrentProjectId(projectId);
        setCurrentProjectName(name);
        setCurrentProjectDescription(description);
    };

    const handleGoogleLoginSuccess = async (credentialResponse: any) => {
        console.log('Login exitoso en App.tsx:', credentialResponse);

        try {
            // Envía el token de ID al backend PHP para verificación
            const response = await fetch(`${config.API_BASE_URL}/auth/google-login.php`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ idToken: credentialResponse.credential }),
            });

            const data = await response.json();
            if (response.ok && data.success) {
                console.log('Respuesta del backend (usuario autenticado):', data);
                // Si el backend confirma la autenticación y devuelve datos del usuario

                setIsAuthenticated(true);
                setUserProfile(data.user); // Asume que tu backend devuelve un objeto 'user'
                setUserProjects(data.projects || []);
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('userProfile', JSON.stringify(data.user));
                localStorage.setItem('userProjects', JSON.stringify(data.projects || []));

                // If there was a pending share token saved before login, consume it now
                const pending = pendingShareToken;
                if (pending) {
                    try {
                        const resp = await fetch(`${config.API_BASE_URL}/share/consume-share-link.php`, {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ token: pending })
                        });
                        const pdata = await resp.json();
                        if (pdata.success && pdata.new_project_id) {
                            // Open the copied project
                            handleOpenProject(pdata.new_project_id, pdata.name || '', pdata.description || '');
                        } else {
                            console.warn('Could not consume pending share token', pdata.message || pdata);
                        }
                    } catch (err) {
                        console.error('Error consuming pending share token', err);
                    } finally {
                        setPendingShareToken(null);
                    }
                }

                console.log('Usuario autenticado:', data);

                // Aquí podrías guardar el authToken si tu backend lo devuelve
                // localStorage.setItem('authToken', data.authToken);
            } else {
                console.error('Error del backend o token inválido:', data.message || 'Error desconocido');
                alert('No se pudo iniciar sesión. Inténtalo de nuevo.');
                setIsAuthenticated(false);
                setUserProfile(null);
            }
        } catch (error) {
            console.error('Error al comunicarse con el backend:', error);
            alert('Error de red. Inténtalo de nuevo más tarde.');
            setIsAuthenticated(false);
            setUserProfile(null);
        }
    };

    const handleGoogleLoginFailure = (error: any) => {
        console.error('Error en el login de Google en App.tsx:', error);
        alert('No se pudo completar el inicio de sesión con Google.');
        setIsAuthenticated(false);
        setUserProfile(null);
    };

    const handleBackToDashboard = () => {
        setCurrentProjectId(null);
        setCurrentProjectName(null);
        setCurrentProjectDescription(null);
        if (userProfile?.id) {
            fetchUserProjects(userProfile.id);
        }
    };

    const handleLogout = async() => {
        // Limpiar el estado de autenticación y el almacenamiento local
        setIsAuthenticated(false);
        setUserProfile(null);
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userProfile');

        // Si usas el paquete @react-oauth/google, también puedes usar googleLogout() si necesitas limpiar más a fondo.
        // import { googleLogout } from '@react-oauth/google';
        // googleLogout();

        const response = await fetch(`${config.API_BASE_URL}/auth/logout.php`, {
            method: 'POST',
            credentials: 'include', 
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        if (data.message ) {
            console.log('Sesión cerrada correctamente:', data.message);
        }

    };

    const fetchUserProjects = async (userId: number) => {
        const response = await fetch(`${config.API_BASE_URL}/auth/get-projects.php`, {
            method: 'POST',
            credentials: 'include', 
            headers: { 'Content-Type': 'application/json' }
        });
        // console.log('response de get-projects.php:');
        // console.log(response);
        const data = await response.json();
        // console.log('Proyectos del usuario obtenidos:', data);
        if (data.success && data.projects) {
            setUserProjects(data.projects);
            localStorage.setItem('userProjects', JSON.stringify(data.projects));
        }
    };

    return (
        <GoogleOAuthProvider clientId={config.GOOGLE_CLIENT_ID}>
            <div style={appContainerStyle}>
                {!isAuthenticated ? (
                    <LoginPage
                        onSuccess={handleGoogleLoginSuccess}
                        onFailure={handleGoogleLoginFailure}
                    />
                ) : currentProjectId ? (
                    <DocumentDataProvider>
                        <ProjectPageB
                            userProfile={userProfile}
                            onLogout={handleLogout}
                            onBackToDashboard={handleBackToDashboard}
                            projectId={currentProjectId}
                            projectName={currentProjectName || ''}
                            projectDescription={currentProjectDescription || ''}
                        />
                    </DocumentDataProvider>
                ) : (
                    <DashboardPage
                        userProfile={userProfile}
                        userProjects={userProjects}
                        onLogout={handleLogout}
                        onOpenProject={(id: number, name: string, description: string) =>
                            handleOpenProject(id, name, description)
                        }
                    />
                )}
            </div>
        </GoogleOAuthProvider>
    );
};


const appContainerStyle: React.CSSProperties = {
    fontFamily: '"Overleaf Sans", sans-serif', 
    // minHeight: '95vh',
    height: 'calc(100vh - 20px)',
    backgroundColor: '#f6f6f6', // Fondo claro
    color: '#333',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start', // Alinea el contenido arriba
    // paddingTop: '20px',
    margin: '10px',
};

export default App;

